/* eslint-disable react/react-in-jsx-scope */
import { Input } from "@/components/ui/input";
import { convertEuroToCurrency } from "@/libs/amount";
import { gToKg } from "@/libs/formule";
import { cn } from "@/libs/utils";
import { FileText } from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface IndicatorType {
  project: number;
  indicator: number;
  isPrice: boolean;
  year: number;
  type: number;
  value: string | number | null;
  month: number;
  currency: any;
  unity: any;
  correspondance: any;
  setIndicatorDataVerify?: (data: any) => void;
  setOpenModalVerify?: (isOpen: boolean) => void;
}

const formatValueForDisplay = (value: string | number | null): string => {
  if (value === null || value === "-") return "-";
  const num = Number(String(value).replace(",", "."));
  if (isNaN(num)) return "-";
  return num.toLocaleString("fr-FR");
};

const FieldInput: FC<{
  defaultValue: any;
  indicatorData: IndicatorType;
  handleChange: (indicator: IndicatorType) => void;
  isDisabled: boolean;
  isReadOnly: boolean;
  dateOldRate: any;
  oldRate: any;
  isFutur?: boolean;
}> = ({
  defaultValue,
  handleChange,
  indicatorData,
  isDisabled,
  isReadOnly,
  dateOldRate,
  oldRate,
  isFutur,
}) => {
  const [displayedValue, setDisplayedValue] = useState("-");
  const [isEditing, setIsEditing] = useState(false);

  const oldValueRef = useRef<string | number | null>(null);
  const prevDepsRef = useRef<{
    value: string | number | null;
    defaultValue: any;
    oldRate: any;
    unity: string | null;
    currency: string | null;
  }>({
    value: null,
    defaultValue: null,
    oldRate: null,
    unity: null,
    currency: null,
  });

  const [showRate, setShowRate] = useState(true);

  useEffect(() => {
    const baseValue =
      indicatorData.value !== null ? indicatorData.value : defaultValue;
    const unity = indicatorData?.unity?.toLowerCase();
    const currency = indicatorData?.currency?.code;

    if (
      prevDepsRef.current.value !== baseValue ||
      prevDepsRef.current.defaultValue !== defaultValue ||
      prevDepsRef.current.oldRate !== oldRate ||
      prevDepsRef.current.unity !== unity ||
      prevDepsRef.current.currency !== currency
    ) {
      const processValue = async () => {
        if (baseValue === null || baseValue === "-" || isDisabled) {
          setDisplayedValue("-");
          return;
        }

        const unity = indicatorData?.unity?.toLowerCase();
        if (unity) {
          let convertedValue = baseValue;
          if (unity === "kg" || unity === "m3" || unity === "m³") {
            convertedValue = gToKg(baseValue as any);
          }
          setDisplayedValue(String(convertedValue));
          return;
        }

        if (indicatorData.isPrice && indicatorData.currency?.code !== "EUR") {
          try {
            const rate = indicatorData.type === 3 ? oldRate : null;
            const res = await convertEuroToCurrency(
              baseValue as number,
              indicatorData.currency,
              rate
            );
            setDisplayedValue(parseFloat(res.toString()).toFixed(1));
          } catch {
            setDisplayedValue(String(baseValue));
          }
        } else {
          const numValue = parseFloat(baseValue.toString());
          setDisplayedValue(isNaN(numValue) ? "-" : numValue.toFixed(1));
        }
      };

      processValue();
      prevDepsRef.current = {
        value: baseValue,
        defaultValue,
        oldRate,
        unity,
        currency,
      };
    }
  }, [
    indicatorData.value,
    defaultValue,
    oldRate,
    indicatorData?.unity,
    indicatorData.currency,
  ]);

  const inputClassName = useMemo(() => {
    const baseStyle =
      "focus-visible:bg-background h-auto rounded-[3px] p-1 text-center text-[10px] leading-3 focus-visible:ring-0 focus-visible:ring-transparent";
    const typeStyles = {
      1: "bg-verdeoblue text-background focus-visible:border-verdeoblue focus-visible:text-verdeoblue",
      2: "bg-verdeogreen text-background focus-visible:border-verdeogreen focus-visible:text-verdeogreen",
      3: "bg-verdeoorange text-background focus-visible:text-verdeoorange",
    };
    const emptyStyle = {
      1: "bg-background text-verdeoblue border border-verdeoblue",
      2: "bg-background text-verdeogreen border border-verdeogreen",
      3: "bg-background text-verdeoorange border border-verdeoorange",
    };

    const currentTypeStyle =
      typeStyles[indicatorData.type as keyof typeof typeStyles] || "";
    const currentEmptyStyle =
      displayedValue === "-"
        ? emptyStyle[indicatorData.type as keyof typeof emptyStyle] || ""
        : "";

    return cn(baseStyle, currentTypeStyle, currentEmptyStyle, {
      "pointer-events-none opacity-15": isDisabled,
      "pointer-events-none ": isReadOnly,
    });
  }, [indicatorData.type, displayedValue, isDisabled, isReadOnly]);

  const handleVerification = useCallback(() => {
    if (
      indicatorData.type === 3 &&
      indicatorData.setOpenModalVerify &&
      indicatorData.setIndicatorDataVerify
    ) {
      indicatorData.setIndicatorDataVerify({
        ...indicatorData,
        value: displayedValue,
        dateOldRate,
        oldRate,
      });
      indicatorData.setOpenModalVerify(true);
    }
  }, [indicatorData, displayedValue, dateOldRate, oldRate]);

  const handleFocus = () => {
    if (
      document.getElementById("rate-view")?.parentNode &&
      indicatorData.type === 2 &&
      indicatorData.indicator === 1 &&
      indicatorData.value !== null &&
      indicatorData.value !== "-" &&
      indicatorData.value !== 0 &&
      oldRate !== null &&
      oldRate !== 0 &&
      dateOldRate !== null
    ) {
      document.getElementById("rate-view").innerHTML =
        "Cette valeur a été calculée avec le taux de change en vigueur le " +
        new Date(dateOldRate).toLocaleDateString() +
        "<br />" +
        1 +
        " € = " +
        (1 / oldRate).toLocaleString("fr-FR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        }) +
        " " +
        indicatorData.localCurrency.symbol;
      document.getElementById("rate-view").parentNode.style.display = "flex";
    }
    setIsEditing(true);
    oldValueRef.current = displayedValue;
    if (displayedValue === "-") {
      setDisplayedValue(""); // champ vide
    }
    handleVerification();
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    const input = e.target.value;
    if (input === "-") {
      setDisplayedValue("-");
      return;
    }
    const cleaned = input.replace(/\s/g, "").replace(",", ".");
    if (/^\d*\.?\d*$/.test(cleaned)) {
      setDisplayedValue(cleaned);
    }
  };

  const handleBlur = () => {
    if (document.getElementById("rate-view")?.parentNode) {
      document.getElementById("rate-view").parentNode.style.display = "none";
    }
    setIsEditing(false);

    // Si l'utilisateur n'a rien changé, on ne sauvegarde pas
    if (
      displayedValue === oldValueRef.current ||
      (displayedValue === "" && oldValueRef.current === "-")
    ) {
      // Si affichage vide, on remet simplement "-"
      if (displayedValue === "") {
        setDisplayedValue("-");
      }
      return; // ⛔ stop, pas de handleChange
    }

    // Si la valeur est vide, on remet "-" et on sauvegarde null
    if (displayedValue === "" || displayedValue === "-") {
      setDisplayedValue("-");
      handleChange({ ...indicatorData, value: "-" });
      return;
    }

    if (displayedValue !== oldValueRef.current) {
      const valueToSave =
        displayedValue === "-"
          ? null
          : String(displayedValue).replace(",", ".");
      if (valueToSave !== null) {
        handleChange({ ...indicatorData, value: valueToSave });
      }
    }
  };

  return (
    <>
      <div className="relative flex-1">
        {indicatorData.type === 3 &&
          indicatorData.pjs !== undefined &&
          indicatorData.pjs.length > 0 &&
          Number(displayedValue) > 0 && (
            <div
              className="cursor-pointer flex items-center justify-center border border-[#777] bg-[#fff] absolute size-4 rounded-full"
              style={{ left: "calc(100% - 12px)", bottom: "calc(100% - 12px)" }}
              onClick={handleVerification}
            >
              <FileText color="#777" size={11} />
            </div>
          )}
        <Input
          value={
            isEditing
              ? displayedValue ?? "" // en édition, vide si "-"
              : formatValueForDisplay(displayedValue) // sinon formaté
          }
          onChange={handleChangeInput}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={cn(
            inputClassName,
            isFutur ? "pointer-events-none opacity-45 " : ""
          )}
          disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0} // 🔹 évite que Tab aille dessus
        />
      </div>
    </>
  );
};

export default FieldInput;
